<template>
	<!-- -------- Compact regions list -------- -->
	<div v-if="compact" class="ReportMetaRegions">
		<!-- Region icon -->
		<CloIcon symbol="region" size="sm" class="ReportMetaRegions-icon" />

		<!-- World level -->
		<ul v-if="compactRegionLevel == 'world'" class="ReportMetaRegions-parent u-regions-inline">
			<!-- Countries -->
			<li v-for="country in countries" :key="country.code" class="">
				<!-- Subnat1 -->
				<Region :region="country" :compact="true" />
			</li>
			<CloButton @click.native="showDialog" size="xs" class="Button--hollow">
				<span>{{ $t('country_desc') }}</span>
			</CloButton>
		</ul>

		<!-- Country level -->
		<ul v-else-if="compactRegionLevel == 'country'" class="ReportMetaRegions-parent">
			<li>
				<!-- Country -->
				<strong>
					<Region :region="countries[0]" :compact="true" />
				</strong>
				<ul v-if="countries[0].subregions" class="ReportMetaRegions-child u-regions-inline">
					<!-- For all subnat1's... -->
					<li v-for="subnat1 in countries[0].subregions" :key="subnat1.code" class="">
						<!-- Subnat1 -->
						<Region :region="subnat1" :compact="true" />
					</li>
					<CloButton v-if="countries[0].subregions[0].subregions" @click.native="showDialog" size="xs" class="Button--hollow">
						<span>{{ $t('country_desc') }}</span>
					</CloButton>
				</ul>
			</li>
		</ul>

		<!-- Subnat1 level -->
		<ul v-else-if="compactRegionLevel == 'subnat1'" class="ReportMetaRegions-parent">
			<li>
				<!-- Subnat1 -->
				<strong>
					<Region :region="countries[0].subregions[0]" :parent="countries[0]" :showFullName="true" :compact="true" />
				</strong>
				<ul v-if="countries[0].subregions[0].subregions" class="ReportMetaRegions-child u-regions-inline">
					<!-- For all subnat1's... -->
					<li v-for="subnat2 in countries[0].subregions[0].subregions" :key="subnat2.code" class="">
						<!-- Subnat1 -->
						<Region :region="subnat2" :compact="true" />
					</li>
				</ul>
			</li>
		</ul>

		<!-- Subnat2 level -->
		<ul v-else-if="compactRegionLevel == 'subnat2'" class="ReportMetaRegions-parent">
			<li>
				<!-- Subnat2 -->
				<Region
					:region="countries[0].subregions[0].subregions[0]"
					:parent="countries[0].subregions[0]"
					:grandparent="countries[0]"
					:showFullName="true"
					:compact="true"
				/>
			</li>
		</ul>

		<!-- Full regions dialog -->
		<CloDialog v-show="dialogOpen" id="regions-dialog" class="u-color-vars" @close="closeDialog">
			<template #header>
				<CloHeading level="4" size="2">{{ $t('generic.Regions') }}</CloHeading>
			</template>
			<template #body>
				<ul class="ReportMetaRegions-countries">
					<!-- <li>Level: {{ compactRegionLevel }}</li> -->
					<!-- For all countries... -->
					<li v-for="country in countries" :key="country.code" class="">
						<!-- Country -->
						<Region :region="country" :showSubregionsLabel="true" />
						<!-- If country has subregions... -->
						<ul v-if="country.subregions" class="ReportMetaRegions-subnat1s">
							<!-- For all subnat1's... -->
							<li v-for="subnat1 in country.subregions" :key="subnat1.code" class="">
								<!-- Subnat1 -->
								<Region :region="subnat1" :parent="country" />
								<!-- If subnat1 has subregions -->
								<ul v-if="subnat1.subregions" class="ReportMetaRegions-subnat2s">
									<!-- Subnat2's -->
									<li v-for="subnat2 in subnat1.subregions" :key="subnat2.code">
										<Region :region="subnat2" :parent="subnat1" :grandparent="country" />
									</li>
								</ul>
							</li>
						</ul>
					</li>
				</ul>
			</template>
		</CloDialog>
	</div>
</template>

<script>
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloButton from '@/base/CloButton/CloButton.vue';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloDialog from '@/base/CloDialog/CloDialog.vue';
import Region from './Region.vue';

export default {
	name: 'ReportMetaRegions',
	components: {
		CloIcon,
		CloButton,
		CloHeading,
		CloDialog,
		Region,
	},
	props: {
		regions: Object,
		compact: Boolean,
	},
	computed: {
		countries() {
			let countries = [];
			// Get list of countries from world
			if (this.regions['world']) {
				countries = this.regions['world'];
				// For each country
				for (let country of countries) {
					// Check for this country's array
					if (this.regions[country.code]) {
						// Add subnat1 array as a property of this country
						country.subregions = this.regions[country.code];
						// Check for this subnat1's array
						for (let subnat1 of country.subregions) {
							const testing = false;
							const subnat2Example = [
								{ code: 'AA', name: 'Subnat2' },
								{ code: 'BB', name: 'Subnat2' },
							];
							if (this.regions[subnat1.code]) {
								subnat1.subregions = this.regions[subnat1.code];
							} else if (testing) {
								subnat1.subregions = subnat2Example;
							}
						}
					}
				}
			}
			return countries;
		},
		compactRegionLevel() {
			let level = 'world'; // default for multiple countries
			const countries = this.countries;
			// If one country...
			if (countries && countries.length == 1) {
				level = 'country'; // multiple subnat1's
				const subnat1s = countries[0].subregions;
				// If just one subnat1
				if (subnat1s && subnat1s.length == 1) {
					level = 'subnat1'; // multiple subnat2's
					const subnat2s = subnat1s[0].subregions;
					// if just one subnat2
					if (subnat2s && subnat2s.length == 1) {
						level = 'subnat2';
					}
				}
			}
			return level;
		},
	},
	data() {
		return {
			baseUrl: window.baseUrl,
			dialogOpen: false,
			dialogOpener: null,
		};
	},
	methods: {
		showDialog(e) {
			this.dialogOpen = true;
			this.dialogOpener = e.target;
		},
		closeDialog() {
			this.dialogOpen = false;
			// important: put focus back on element that triggered the dialog
			if (this.dialogOpener) {
				this.dialogOpener.focus();
			}
		},
	},
};
</script>

<style lang="scss">
.ReportMetaRegions {
	display: flex;
	gap: $space-sm;
	@supports not (gap: 1px) {
		> *:not(:last-child) {
			@include inline-sm;
		}
	}
	.ReportMetaRegions-icon.Icon--region {
		color: var(--color-text-muted);
		opacity: 0.5;
		transform: translateY(0.15em);
		flex-shrink: 0;
	}
	ul.u-regions-inline {
		display: flex;
		flex-wrap: wrap;
		column-gap: $space-rel-sm;
		margin: 0;
		[dir='rtl'] & { 
			margin: 0; 
		}
		li:not(:last-of-type)::after {
			color: var(--color-text-muted);
			opacity: 0.5;
			content: '|';
			margin-left: $space-rel-sm;
			[dir='rtl'] & { 
				margin-left: 0; 
				margin-right: $space-rel-sm; 
			}
		}
	}
	a,
	.Button,
	.Button.Button--hollow {
		color: var(--color-link);
		border-color: var(--color-link-border);
		a:link,
		a:visited {
			color: var(--color-link);
			border-color: var(--color-link-border);
		}
		a:hover,
		a:active {
			color: var(--color-link-hover);
			border-color: var(--color-link-hover);
		}
	}
	.Button.Button--hollow {
		margin: 0;
		padding: $space-rel-xs $space-rel-sm;
		// transform: translateY(-.2em);
		display: inline-flex;
		align-items: center;
		align-self: center;
		gap: $space-xs;
		.Icon {
			width: 1.25em;
			height: 1.25em;
		}
	}
}

.ReportMetaRegions-parent,
.ReportMetaRegions-child,
.ReportMetaRegions-countries,
.ReportMetaRegions-subnat1s,
.ReportMetaRegions-subnat2s {
	@include text(3, loose);
	list-style: none;
	margin: 0;
	[dir='rtl'] & {
		margin: 0;
	}
}

.ReportMetaRegions-child,
.ReportMetaRegions-subnat1s,
.ReportMetaRegions-subnat2s {
	margin-left: $space-md;
	[dir='rtl'] & {
		margin-left: 0;
		margin-right: $space-md;
	}
}

.print {
	.ReportMetaRegions {
		.Icon {
			opacity: 1;
			// width: 1.5rem;
			// height: 1.5rem;
		}
		color: var(--color-text);
		a {
			color: var(--color-text);
		}
		.Button {
			display: none;
		}
		.ReportMetaRegions-parent,
		.ReportMetaRegions-child,
		.ReportMetaRegions-countries,
		.ReportMetaRegions-subnat1s,
		.ReportMetaRegions-subnat2s {
			// @include text(4, loose);
		}
	}
}
</style>
